import { CriticalAlertModal } from 'pages/OccurrencePage/_components/CriticalAlertModal';
import { createContext, FC, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { registerOccurrenceActionAsync } from 'services/OccurrenceService';
import { useCriticalOccurrenceSocket } from 'services/OccurrenceService/hooks/useSocket/useCriticalOccurrenceSocket';
import { ICriticalAlertPayload } from 'services/OccurrenceService/types';
import { AppContext } from './AppContextProvider';
import { OCCURRENCE_KEYS } from 'services/OccurrenceService/hooks/useQuery';
import { useQueryClient } from 'react-query';

export interface CriticalOccurrenceContextValue {
  latestAlert: ICriticalAlertPayload | null;
  clearAlert: () => void;
}

const CriticalOccurrenceContext = createContext<CriticalOccurrenceContextValue | null>(null);

export const useCriticalOccurrence = (): CriticalOccurrenceContextValue => {
  const ctx = useContext(CriticalOccurrenceContext);
  if (!ctx) {
    throw new Error('useCriticalOccurrence must be used within CriticalOccurrenceProvider');
  }
  return ctx;
};

export const CriticalOccurrenceProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { sendBrowserNotification, showSnackbar } = useContext(AppContext);
  const queryClient = useQueryClient(); // 🚀 1. Instância do QueryClient

  const [alertQueue, setAlertQueue] = useState<ICriticalAlertPayload[]>([]);
  const activeAlert = alertQueue[0] || null;

  useCriticalOccurrenceSocket({
    enabled: true, 
    onAlertReceived: (payload) => {
      setAlertQueue((prevQueue) => {
        const existingIndex = prevQueue.findIndex((alert) => alert.occurrenceId === payload.occurrenceId);

        // Se já existe um alerta na fila para essa ocorrência, atualiza os dados dele (ex: a foto
        // real chegando alguns segundos depois da primeira notificação, sem foto/com fallback) em
        // vez de descartar a mensagem nova — senão o modal fica preso nos dados do primeiro alerta.
        if (existingIndex !== -1) {
          const updatedQueue = [...prevQueue];
          updatedQueue[existingIndex] = payload;
          return updatedQueue;
        }

        return [...prevQueue, payload];
      });

      sendBrowserNotification(
        "🚨 Alerta Crítico", 
        `Ocorrência #${payload.occurrenceId} - ${payload.personName}`
      );

      if (payload.occurrenceId) {
        queryClient.invalidateQueries([
          OCCURRENCE_KEYS.ALL, 
          OCCURRENCE_KEYS.DETAIL, 
          payload.occurrenceId
        ]);
      }

      queryClient.invalidateQueries([OCCURRENCE_KEYS.ALL, OCCURRENCE_KEYS.LIST]);
      queryClient.invalidateQueries([OCCURRENCE_KEYS.ALL, OCCURRENCE_KEYS.COUNT]);
      queryClient.invalidateQueries([OCCURRENCE_KEYS.ALL, OCCURRENCE_KEYS.BLOCKED_LOGS]);
      queryClient.invalidateQueries([OCCURRENCE_KEYS.ALL, OCCURRENCE_KEYS.BLOCKED_LOGS_COUNT]);
    }
  });

  const handleNextAlert = useCallback(() => {
    setAlertQueue((prevQueue) => prevQueue.slice(1));
  }, []);

  const handleRegisterAction = async (actionText: string) => {
    if (!activeAlert) return;
    
    try {
      await registerOccurrenceActionAsync({
        id: activeAlert.occurrenceId,
        occurrenceId: activeAlert.occurrenceId,
        data: { actionTaken: actionText }
      });
      
      showSnackbar("Providência registrada com sucesso!", "success");
      
      // 🚀 3. Re-invalida para garantir que a providência recém-salva apareça imediatamente
      queryClient.invalidateQueries([
        OCCURRENCE_KEYS.ALL, 
        OCCURRENCE_KEYS.DETAIL, 
        activeAlert.occurrenceId
      ]);

      handleNextAlert();
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Erro ao registrar a providência.";
      showSnackbar(errorMessage, "error");
    }
  };

  const value: CriticalOccurrenceContextValue = useMemo(() => ({
    latestAlert: activeAlert,
    clearAlert: handleNextAlert
  }), [activeAlert, handleNextAlert]);

  return (
    <CriticalOccurrenceContext.Provider value={value}>
      {children}
      <CriticalAlertModal
        open={!!activeAlert}
        onClose={handleNextAlert}
        alertData={activeAlert}
        onRegisterAction={handleRegisterAction}
      />
    </CriticalOccurrenceContext.Provider>
  );
};